import React from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import type { SimpleNamedItem } from "../../../types";
import { RichText } from "../../../../components/RichText";

interface ConnectedCoursesScreenProps {
  courses: SimpleNamedItem[];
  onNext: () => void;
}

const Container = styled(Box, {
  label: "ConnectedCoursesContainer",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  alignItems: "flex-start",
  padding: theme.spacing(6, 8),
  maxWidth: 1280,
  width: "100%",
  margin: "0 auto",
}));

const Title = styled(Typography, {
  label: "ConnectedCoursesTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const CourseGrid = styled(Box, {
  label: "ConnectedCoursesGrid",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  gap: theme.spacing(7),
  alignItems: "center",
  justifyContent: "center",

  width: "100%",
  flexWrap: "wrap",
}));

const CourseCard = styled(Box, {
  label: "ConnectedCourseCard",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  width: 320,
}));

const CourseIcon = styled("img", {
  label: "ConnectedCourseIcon",
})(() => ({
  width: 88,
  height: 88,
  objectFit: "contain",
}));

const CourseDescription = styled(Typography, {
  label: "ConnectedCourseDescription",
})(() => ({
  fontFamily: "Proxima Nova",
  fontSize: "18px",
  lineHeight: "24px",
  color: "#ffffff",
  margin: 0,
  marginLeft: -8,
}));

const CourseTitle = styled(Typography, {
  label: "ConnectedCourseTitle",
})(() => ({
  fontFamily: "Proxima Nova",
  marginTop: 15,
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "24px",
  color: "#ffffff",
}));

const NavContainer = styled(Box, {
  label: "ConnectedCoursesNav",
})(() => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
}));

const NextButton = styled(Button, {
  label: "ConnectedCoursesNextButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

export const ConnectedCoursesScreen = ({
  courses,
  onNext,
}: ConnectedCoursesScreenProps) => {
  return (
    <Container>
      <Title>Connected Courses</Title>

      <CourseGrid>
        {courses.map((course) => (
          <CourseCard key={course.id}>
            {course.icon?.url && (
              <CourseIcon src={course.icon.url} alt={course.name} />
            )}
            <CourseTitle>{course.name}</CourseTitle>
            <CourseDescription>
              <RichText value={course.description as string} />
            </CourseDescription>
          </CourseCard>
        ))}
      </CourseGrid>

      <NavContainer>
        <NextButton variant="contained" onClick={onNext}>
          Next
        </NextButton>
      </NavContainer>
    </Container>
  );
};
